import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { selectCurrentCompany, fetchUserCompanies, resetStore } from 'reducers/auth';
// components
import Preloader from 'components/Preloader';
import PageMessage from 'components/PageMessage';
import LinkUnavailable from 'components/LinkUnavailable';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import omit from 'lodash.omit';

// Links from older notifications may carry a relative path, which would resolve under /switch-company,
// or their own query inside redirect_url, which the router would otherwise treat as part of the path
const getRedirectLocation = (query) => {
  const [path, search = ''] = decodeURIComponent(query.redirect_url || '/').split('?');
  const redirectQuery = {};
  new URLSearchParams(search).forEach((value, key) => { redirectQuery[key] = value; });
  return {
    pathname: path.startsWith('/') ? path : `/${path}`,
    query: { ...redirectQuery, ...omit(query, 'redirect_url') },
  };
};

const CompanySwitcher = ({
  fetchUserCompanies,
  location: { query },
  params,
  resetStore,
  router,
  selectCurrentCompany,
}) => {
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    resetStore();
    selectCurrentCompany(params.id)
      .then((user) => {
        if (!user.current_company.is_expired) fetchUserCompanies();
        // Replace: Back must not land on this page again and switch the company once more
        router.replace(getRedirectLocation(query));
      })
      .catch((errors) => {
        // A dropped connection is not a broken link, the same link works after a refresh
        if (errors?.isTypeError || errors?.isAbortError) {
          setFailure('network');
          return;
        }
        toastResponseErrors(errors);
        setFailure('unavailable');
      });
  }, []);

  if (failure === 'network') {
    return (
      <PageMessage
        titleId="component.errorBoundary.title"
        descriptionId="component.errorBoundary.description"
        actionLabelId="general.refresh"
        onAction={() => window.location.reload()}
      />
    );
  }

  if (failure === 'unavailable') return <LinkUnavailable />;

  return <Preloader isActive />;
};

CompanySwitcher.propTypes = {
  params: T.object.isRequired,
  location: T.object.isRequired,
  router: T.object.isRequired,
  selectCurrentCompany: T.func.isRequired,
  fetchUserCompanies: T.func.isRequired,
  resetStore: T.func.isRequired,
};

export default connect(
  null, {
    selectCurrentCompany,
    fetchUserCompanies,
    resetStore,
  }
)(CompanySwitcher);
